/**
 * The health row: one score per subject, read from the nightly cache.
 *
 * Read-only and period-independent. The score is computed by the anomaly run
 * over a fixed 30-day window (see backend/chainlit/health_score.py), so it
 * deliberately does NOT move when the dashboard's period chips change — which
 * is why the row renders above them rather than below.
 */

export type HealthBand = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type NotMeasured = 'no_rules' | 'no_data';

/** One open finding and what it cost, with the arithmetic that produced it. */
export interface HealthFinding {
  anomaly_id: number;
  metric_name: string | null;
  severity: HealthBand;
  status: string;
  timeframe: string | null;
  delta_pct: number | null;
  detected_at: string | null;
  severity_points: number;
  impact_share: number | null;
  impact_multiplier: number;
  points: number;
}

export interface HealthInputs {
  version?: number;
  scored_day?: string;
  rule_count?: number;
  starting_score?: number;
  reason?: string;
  data_presence?: 'unverified';
  arithmetic?: string;
  findings?: {
    measured: boolean;
    open_count: number;
    points: number;
    cap: number;
    capped: boolean;
  };
  freshness?: {
    measured: boolean;
    reason?: string;
    last_update_date: string | null;
    stale_days: number | null;
    grace_days?: number;
    points_per_day?: number;
    points: number;
    cap: number;
  };
  quality?: {
    measured: boolean;
    reason?: string;
    blocked_columns: { column: string; issue: string | null; affected_pct: number | null; reason: string }[];
    points_per_column?: number;
    points: number;
    cap: number;
  };
}

export interface HealthScore {
  subject: string;
  subject_label: string | null;
  score: number | null;
  band: HealthBand | null;
  not_measured: NotMeasured | null;
  inputs: HealthInputs;
  findings: HealthFinding[];
  computed_at: string | null;
  /** Set when the caller is owner-scoped: the score stands, the links do not. */
  scoped?: boolean;
}

export async function apiHealthScores(): Promise<{ items: HealthScore[] }> {
  const res = await fetch('/api/health-scores');
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * A short human name for the subject.
 *
 * `subject_label` is the tenant's `dfs.description`, and that text is written
 * for the model, not for a person: it names columns, states which figure to
 * quote, and runs to a paragraph. Rendered raw it filled the panel header with
 * schema before the reader reached a single number.
 *
 * The house shape for those descriptions is "Short name - the long
 * explanation", so the first clause is the name. Falling back through the
 * sentence break and then a hard cap means a tenant that never adopted the
 * convention still gets something short rather than a wall.
 */
export function subjectTitle(s: HealthScore): string {
  const label = s.subject_label?.trim();
  if (label) {
    const head = label.split(/\s+[\u2014\u2013-]\s+/)[0].split(/\.\s/)[0].trim();
    if (head && head.length <= 70) return head;
    return head.slice(0, 67).trimEnd() + '\u2026';
  }
  return s.subject
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Everything after the short name - shown only if the reader asks for it. */
export function subjectDetail(s: HealthScore): string | null {
  const label = s.subject_label?.trim();
  if (!label) return null;
  const head = subjectTitle(s).replace(/\u2026$/, '');
  const rest = label.startsWith(head) ? label.slice(head.length) : label;
  const clean = rest.replace(/^\s*[\u2014\u2013-]\s*/, '').trim();
  return clean.length > 0 ? clean : null;
}


export const NOT_MEASURED_COPY: Record<NotMeasured, string> = {
  // Deliberately not "healthy" and not "failing". A subject nobody watches is
  // unobserved, and saying so is the point — it is the one state the owner can
  // actually fix from here.
  no_rules: 'No rules watch this subject yet',
  no_data: 'No data in the scored period',
};
